<template lang="pug">
h1.mb-4(v-if="actions.length > 0") {{ $t("actions") }}
el-button.mb-4(
  v-for="action in actions",
  :key="action.label",
  :type="action.type",
  v-loading="activeActionLabel === action.label",
  :disabled="activeActionLabel !== null",
  @click="handleButtonAction(action)"
) {{ action.label }}
el-dialog(
  v-model="showCommentDialog",
  :title="dialogOptions.title",
  @close="closeDialog"
)
  smx-form
    smx-form-item(
      v-if="dialogOptions.showComment",
      :label="$t('comment')",
      :required="dialogOptions.commentMandatory"
    )
      el-input(v-model="comment")
    smx-form-item(
      v-if="dialogOptions.showLongComment",
      :label="$t('longComment')"
    )
      el-input(v-model="longComment", :rows="3", type="textarea")
    smx-form-item(
      v-if="dialogOptions.showWorkCarriedOut",
      :label="$t('workCarriedOut')",
      required
    )
      el-input(v-model="localWorkCarriedOut", :rows="3", type="textarea")
    smx-form-item(
      v-if="dialogOptions.showWorkDuration",
      :label="$t('workDuration')",
      required
    )
      TwinPropertyInput(
        v-if="props.workDurationProperty",
        v-model="localWorkDuration",
        :prop="props.workDurationProperty"
      )
    smx-form-item(
      v-if="dialogOptions.showAssignee",
      :label="$t('assignee')",
      required
    )
      UserSelect(v-model="assignee")
    smx-form-item(
      v-if="dialogOptions.showStartDate",
      :label="$t('plannedStart')",
      required
    )
      el-date-picker(
        v-model="startDate",
        format="DD/MM/YYYY",
        value-format="YYYY-MM-DD"
      )
    smx-form-item(
      v-if="dialogOptions.showDueDate",
      :label="$t('plannedFinish')",
      required
    )
      el-date-picker(
        v-model="dueDate",
        format="DD/MM/YYYY",
        value-format="YYYY-MM-DD"
      )
  template(#footer)
    el-button(@click="closeDialog") {{ $t("cancel") }}
    el-button(
      v-loading="dialogActionLoading",
      type="primary",
      @click="handleAction(dialogOptions.action)"
    ) {{ dialogOptions.buttonLabel }}
</template>

<script setup lang="ts">
// For possible actions see https://github.com/inctec/smx-foundation/issues/332
import { computed, ref, watch } from "vue";
import type {
  TwinProperty,
  TwinResponse,
} from "@shared/services/twinManagement";
import { useI18n } from "vue-i18n";

import {
  rejectTicket,
  planTicket,
  completeTicket,
  validateTicket,
  submitTicket,
  inProgressTicket,
} from "@shared/maintenance/service";
import { useAuthStore } from "@shared/stores/auth";
import { ElNotification } from "element-plus";
import { useTwinStore } from "@shared/stores/twinManagement";
import UserSelect from "@shared/components/twinManagement/components/UserSelect.vue";
import { statuses } from "../constants";
import TwinPropertyInput from "@shared/components/twinManagement/components/TwinPropertyInput.vue";
import { showResponseErrorToUser } from "@shared/util/genericResponseErrors";
import { type TicketManagementAuth } from "@shared/maintenance/hooks/maintenanceAuth";

const emit = defineEmits(["edit"]);

const props = defineProps<{
  ticket: TwinResponse;
  workCarriedOut?: string;
  workDuration?: string;
  workDurationProperty?: TwinProperty;
  useAuth: () => TicketManagementAuth;
}>();

const tmAuth = props.useAuth();

const { t } = useI18n();

const showCommentDialog = ref(false);

const dialogOptions = ref<{
  title: string;
  buttonLabel: string;
  showLongComment: boolean;
  showWorkCarriedOut?: boolean;
  showWorkDuration?: boolean;
  showComment: boolean;
  showAssignee?: boolean;
  showStartDate?: boolean;
  showDueDate?: boolean;
  workDuration?: string;
  commentMandatory?: boolean;
  action: () => Promise<unknown>;
}>({
  title: "",
  buttonLabel: "",
  showLongComment: false,
  showComment: false,

  commentMandatory: false,
  action: async () => {},
});

const comment = ref("");
const longComment = ref("");

const localWorkCarriedOut = ref(props.workCarriedOut ?? "");

watch(
  () => props.workCarriedOut,
  () => {
    localWorkCarriedOut.value = props.workCarriedOut ?? "";
  },
);

const localWorkDuration = ref(props.workDuration ?? "");

watch(
  () => props.workDuration,
  () => {
    localWorkDuration.value = props.workDuration ?? "";
  },
);

const _assignee = ref<string[] | null>(null);

const assignee = computed<string[]>({
  get() {
    if (_assignee.value !== null) {
      return _assignee.value;
    }
    const value = props.ticket.properties?.find(
      (item) => item.customId === "assignee",
    )?.propertyValue?.value;
    return Array.isArray(value) ? value : [];
  },
  set(val) {
    _assignee.value = val;
  },
});

const _startDate = ref<string | null>(null);

const startDate = computed<string>({
  get() {
    if (_startDate.value !== null) {
      return _startDate.value;
    }
    const value = props.ticket.properties?.find(
      (item) => item.customId === "startDate",
    )?.propertyValue?.value;
    return typeof value === "string" ? value : "";
  },
  set(val) {
    _startDate.value = val;
  },
});

const _dueDate = ref<string | null>(null);
const dueDate = computed<string>({
  get() {
    if (_dueDate.value !== null) {
      return _dueDate.value;
    }
    const value = props.ticket.properties?.find(
      (item) => item.customId === "dueDate",
    )?.propertyValue?.value;
    return typeof value === "string" ? value : "";
  },
  set(val) {
    _dueDate.value = val;
  },
});

const authStore = useAuthStore();

const isCreator = computed(() => {
  return (
    (
      props.ticket.properties?.find((item) => item.customId === "createdBy")
        ?.propertyValue?.value as string[] | undefined
    )?.[0] === authStore.userId
  );
});

const isRequester = computed(() => {
  return (
    (
      props.ticket.properties?.find((item) => item.customId === "requester")
        ?.propertyValue?.value as string[] | undefined
    )?.[0] === authStore.userId
  );
});

const isAssignee = computed(() => {
  return authStore.userId !== null && assignee.value.includes(authStore.userId);
});

const dialogActionLoading = ref(false);

const twinStore = useTwinStore();

async function handleAction(action: () => Promise<unknown>) {
  if (dialogActionLoading.value) {
    return;
  }
  if (dialogOptions.value.showAssignee && assignee.value?.length === 0) {
    ElNotification({
      type: "warning",
      title: "",
      message: `${t("followingFieldsAreRequired")} ${t("assignee")}`,
    });
    return;
  }
  if (dialogOptions.value.showStartDate && startDate.value === "") {
    ElNotification({
      type: "warning",
      title: "",
      message: `${t("followingFieldsAreRequired")} ${t("plannedStart")}`,
    });
    return;
  }
  if (dialogOptions.value.showDueDate && dueDate.value === "") {
    ElNotification({
      type: "warning",
      title: "",
      message: `${t("followingFieldsAreRequired")} ${t("plannedFinish")}`,
    });
    return;
  }
  if (dialogOptions.value.commentMandatory && comment.value === "") {
    ElNotification({
      type: "warning",
      title: "",
      message: `${t("followingFieldsAreRequired")} ${t("comment")}`,
    });
    return;
  }
  if (
    dialogOptions.value.showWorkCarriedOut &&
    (localWorkCarriedOut.value === "" ||
      localWorkCarriedOut.value === undefined)
  ) {
    ElNotification({
      type: "warning",
      title: "",
      message: t("enterWorkCarriedOutBeforeCompleting"),
    });
    return;
  }
  if (
    dialogOptions.value.showWorkDuration &&
    (localWorkDuration.value === "" || localWorkDuration.value === undefined)
  ) {
    ElNotification({
      type: "warning",
      title: "",
      message: t("enterWorkDurationBeforeCompleting"),
    });
    return;
  }

  dialogActionLoading.value = true;
  try {
    await action();
    await twinStore.reloadTwin(props.ticket.uuid, true);
    emit("edit");
    closeDialog();
  } catch (e) {
    showResponseErrorToUser(e, t);
    throw e;
  } finally {
    dialogActionLoading.value = false;
  }
}

function closeDialog() {
  comment.value = "";
  longComment.value = "";
  showCommentDialog.value = false;
}

function submitDraft() {
  dialogOptions.value = {
    title: t("submit"),
    buttonLabel: t("submit"),
    showComment: true,
    showLongComment: true,
    action: () =>
      submitTicket(props.ticket.uuid, comment.value, longComment.value),
  };
  showCommentDialog.value = true;
}

function reject() {
  dialogOptions.value = {
    title: t("reject"),
    buttonLabel: t("reject"),
    showComment: true,
    commentMandatory: true,
    showLongComment: false,
    action: () => rejectTicket(props.ticket.uuid, comment.value),
  };
  showCommentDialog.value = true;
}

function plan() {
  dialogOptions.value = {
    title: t("plan"),
    buttonLabel: t("plan"),
    showComment: true,
    showLongComment: true,
    showAssignee: true,
    showDueDate: true,
    showStartDate: true,
    action: () =>
      planTicket(
        props.ticket.uuid,
        comment.value,
        longComment.value,
        assignee.value[0] ?? "",
        startDate.value,
        dueDate.value,
      ),
  };
  showCommentDialog.value = true;
}

async function takeInProcess() {
  // dialogOptions.value = {
  //   title: t('takeInProcess'),
  //   buttonLabel: t('takeInProcess'),
  //   showComment: false,
  //   showLongComment: false,
  //   showAssignee: true,
  //   action: () => inProgressTicket(props.ticket.uuid, assignee.value[0])
  // }
  // showCommentDialog.value = true;
  await inProgressTicket(props.ticket.uuid);
  await twinStore.reloadTwin(props.ticket.uuid, true);
  emit("edit");
}

function complete() {
  dialogOptions.value = {
    title: t("complete"),
    buttonLabel: t("complete"),
    showComment: true,
    showWorkDuration: true,
    showWorkCarriedOut: true,
    showLongComment: false,
    // TODO: reload data or set data properly
    action: async () => {
      await completeTicket(
        props.ticket.uuid,
        comment.value,
        localWorkCarriedOut.value,
        localWorkDuration.value,
      );
    },
  };
  showCommentDialog.value = true;
}
async function validate() {
  await validateTicket(props.ticket.uuid, true, "");
  await twinStore.reloadTwin(props.ticket.uuid, true);
  emit("edit");
}
function invalidate() {
  dialogOptions.value = {
    title: t("reopen"),
    buttonLabel: t("reopen"),
    showComment: true,
    commentMandatory: true,
    showLongComment: false,
    action: () => validateTicket(props.ticket.uuid, false, comment.value),
  };
  showCommentDialog.value = true;
}

const status = computed(() => {
  const value = props.ticket.properties?.find(
    (item) => item.customId === "status",
  )?.propertyValue?.value;
  return typeof value === "string" ? value : statuses.new;
});

const isTicketTypeManager = computed(
  () => tmAuth.isTicketTypeManager(props.ticket) || isAssignee.value,
);
const isTicketTypeProcessor = computed(() =>
  tmAuth.isTicketTypeProcessor(props.ticket),
);
const isAdmin = computed(() => tmAuth.isAdmin());

const activeActionLabel = ref<string | null>(null);

async function handleButtonAction(action: {
  label: string;
  action: () => void | Promise<unknown>;
}) {
  if (activeActionLabel.value !== null) {
    return;
  }

  activeActionLabel.value = action.label;
  try {
    await action.action();
  } finally {
    activeActionLabel.value = null;
  }
}

const actions = computed(() => {
  if (status.value === statuses.draft && isCreator.value) {
    return [
      {
        type: "primary",
        label: t("submitDraft"),
        action: submitDraft,
      },
    ];
  }
  if (
    status.value === statuses.new &&
    (isTicketTypeProcessor.value || isTicketTypeManager.value)
  ) {
    const options = [
      {
        type: "primary",
        label: t("takeInProcess"),
        action: takeInProcess,
      },
      {
        label: t("plan"),
        type: "default",
        action: plan,
      },
    ];

    if (isTicketTypeManager.value) {
      options.push({
        label: t("reject"),
        type: "danger",
        action: reject,
      });
    }

    return options;
  }

  if (
    status.value === statuses.planned &&
    (isTicketTypeProcessor.value || isTicketTypeManager.value)
  ) {
    return [
      {
        type: "primary",
        label: t("takeInProcess"),
        action: takeInProcess,
      },
    ];
  }
  if (
    status.value === statuses.inProcess &&
    (isTicketTypeProcessor.value || isTicketTypeManager.value)
  ) {
    return [
      {
        type: "primary",
        label: t("complete"),
        action: complete,
      },
    ];
  }
  if (
    status.value === statuses.completed &&
    (isCreator.value ||
      isRequester.value ||
      isTicketTypeManager.value ||
      isAdmin.value)
  ) {
    return [
      {
        type: "primary",
        label: t("validate"),
        action: validate,
      },
      {
        type: "danger",
        label: t("reopen"),
        action: invalidate,
      },
    ];
  }
  return [];
});
</script>
